/** @jsx jsx */

import type {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from 'react';
import { Fragment } from 'react';
import React, { useRef } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSelect } from 'downshift';
import { Box } from '@balance-web/box';
import { jsx } from '@balance-web/core';
import { PopoverDialog, usePopper } from '@balance-web/popover';
import { themeRaw } from '@balance-web/theme';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Divider } from '@balance-web/divider';

import {
  Menu,
  MenuItem,
  MenuItemLoadingIndicator,
  SelectMenuButton,
} from './styled-components';
import { useItemMap } from './useItemMap';

// Prep
// ------------------------------

export const MENU_ALIGNMENT = {
  left: 'bottom-start',
  right: 'bottom-end',
} as const;

// Types
// ------------------------------

type UnionToIntersection<T> = (T extends any ? (x: T) => any : never) extends (
  x: infer R
) => any
  ? R
  : never;

type ItemToLabel<Option> = UnionToIntersection<
  Option extends { label: string }
    ? { itemToLabel?: (option: Option) => string }
    : { itemToLabel: (option: Option) => string }
>;

type ItemToValue<Option> = UnionToIntersection<
  Option extends { value: string | number }
    ? { itemToValue?: (option: Option) => string | number }
    : { itemToValue: (option: Option) => string | number }
>;

export type AnchorElementType =
  | HTMLAnchorElement
  | HTMLButtonElement
  | HTMLDivElement
  | HTMLSpanElement;

type AlignType = keyof typeof MENU_ALIGNMENT;

type TriggerRendererType = (options: {
  isOpen: boolean;
  hasValue: boolean;
  triggerProps: ButtonHTMLAttributes<HTMLButtonElement>;
}) => ReactElement;

export type ModeProps =
  | {
      onLoadMore: () => void;
      canLoadMore: boolean;
      loadingState?: SelectMenuLoadingState;
    }
  | {
      onLoadMore?: never;
      canLoadMore?: never;
      loadingState?: never;
    };

export type SelectMenuLoadingState =
  | 'loading'
  | 'loadingMore'
  | 'error'
  | 'idle';

export type SelectMenuProps<Option> = {
  /** Direction to align the menu dialog relative to the trigger element. */
  align?: AlignType;
  id?: string;
  /** Provide the height, in pixels, of each item in the list. */
  itemHeight?: number;
  /** Control each item is rendered within the menu. */
  itemRenderer?: (option: Option) => ReactElement;
  /** Determine if a given option should be disabled within the menu. */
  itemToDisabled?: (option: Option) => boolean;
  /** Map each option to its appropriate label. */
  itemToLabel?: (option: Option) => string;
  /** Map each option to its appropriate value. */
  itemToValue?: (option: Option) => string | number;
  /** Called when an option is selected. */
  onChange: (value: Option) => void;
  /** Support a "clear" action in the **default** trigger button. */
  onClear?: MouseEventHandler;
  /** Array of options for the user to select from. */
  options: Option[];
  /** The footer item that sticks at the bottom of all the options. */
  footerOption?: Option;
  /** The element to be rendered as the dialog trigger. */
  trigger: ReactNode | TriggerRendererType;
  /** The selected value. Must match the return value from `itemToValue`. */
  value: Option | Array<Option> | undefined;
  /** Show selected values count when value is greater than this value */
  selectedValueThreshold?: number;
  menuLeftOffset?: number;
  /**
   * Provide fixed width for the menu dialog for situations where menu items are clipped due to limited horizontal real estate.
   */
  menuWidth?: number;
} & ItemToLabel<Option> &
  ItemToValue<Option> &
  ModeProps;

// Main export
// ------------------------------

export function useVirtualConfig(itemHeight: number) {
  return {
    estimateSize: useCallback(() => {
      return itemHeight;
    }, [itemHeight]),
    overscan: 4,
  };
}

export function SelectMenu<Option>({
  align = 'left',
  itemRenderer: consumerRenderer,
  itemHeight = 40,

  itemToDisabled = (item: Option) => {
    // @ts-ignore
    return Boolean(item.disabled);
  },
  itemToLabel = (item: Option) => {
    // @ts-ignore
    return item.label;
  },
  itemToValue = (item: Option) => {
    // @ts-ignore
    return item.value;
  },
  onChange,
  onClear,
  options,
  footerOption,
  trigger,
  value,
  canLoadMore,
  onLoadMore,
  loadingState,
  selectedValueThreshold,
  menuLeftOffset = 0,
  menuWidth,
  // @ts-ignore
  ...props
}: SelectMenuProps<Option>) {
  const optionsWithOptionalFooter = footerOption
    ? [...options, footerOption]
    : options;

  // create the default renderer (can't happen in destructuring because we need `itemToLabel`)
  const itemRenderer =
    consumerRenderer ||
    ((item: Option) => {
      return <Fragment>{itemToLabel(item)}</Fragment>;
    });

  // handle downshift type requirement here rather than passing on to consumers
  const itemToString = (item: Option | null) => {
    return item ? itemToLabel(item) : '';
  };

  // setup downshift (selection/navigation handling)
  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items: optionsWithOptionalFooter,
    itemToString,
    selectedItem: Array.isArray(value) ? null : value || null, // stop downshift from disallowing selection of an existing item when multi-select
    isItemDisabled: itemToDisabled,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      if (Array.isArray(value)) {
        switch (type) {
          case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
          case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: true, // keep the menu open after selection
              highlightedIndex: state.highlightedIndex, // maintain the currently highlighted item
            };
        }
      }

      return changes;
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        onChange(selectedItem);
      }
    },
  });

  // setup popper (position handling)
  const {
    setAnchorElement,
    setPopoverElement,
    styles,
    attributes,
  } = useSelectPopper({ align, isOpen, menuLeftOffset });

  const triggerProps = getToggleButtonProps({
    ref: (element: AnchorElementType | null) => {
      return setAnchorElement(element);
    },
  });

  // setup trigger (the button that invokes the menu)
  const hasValue = Boolean(
    Array.isArray(value) ? value?.length : value !== undefined
  );
  const triggerElement =
    typeof trigger === 'function' ? (
      trigger({ isOpen, hasValue, triggerProps })
    ) : (
      <SelectMenuButton
        hasValue={hasValue}
        selectionCount={
          selectedValueThreshold === undefined
            ? 0
            : Array.isArray(value)
            ? value.length > selectedValueThreshold
              ? value.length
              : 0
            : 0
        }
        onClear={onClear}
        {...triggerProps}
        // @ts-ignore
        label={trigger}
      />
    );

  const menuRef = useRef<HTMLDivElement>(null);

  const themedVirtualConfig = useVirtualConfig(itemHeight);
  const rowVirtualizer = useVirtualizer({
    ...themedVirtualConfig,
    count: canLoadMore ? options.length + 1 : options.length,
    getScrollElement: () => {
      return menuRef.current;
    },
  });

  const itemMap = useItemMap({
    getItemProps,
    itemRenderer,
    itemToValue,
    items: optionsWithOptionalFooter,
    selectedItem: value || null,
    highlightedIndex,
  });

  const menuContent = (() => {
    // we need a reference to the menu element to prepare accessibility
    // handlers, but we don't want the menu content to be focusable until the
    // menu is open
    if (!isOpen) {
      return null;
    }

    // render items when available
    if (optionsWithOptionalFooter.length) {
      return (
        <Fragment>
          <div
            key="total-size"
            style={{ height: rowVirtualizer.getTotalSize() }}
          />
          {rowVirtualizer.getVirtualItems().map(itemMap)}
        </Fragment>
      );
    }

    if (loadingState === 'loading' || loadingState === 'loadingMore') {
      return <MenuItemLoadingIndicator style={{ height: itemHeight }} />;
    }

    // if we reach this point something's gone wrong...
    return null;
  })();

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= options.length - 1 &&
      canLoadMore &&
      onLoadMore &&
      loadingState !== 'loadingMore'
    ) {
      onLoadMore();
    }
  }, [canLoadMore, onLoadMore, options.length, loadingState, rowVirtualizer]);

  return (
    <Box {...props}>
      {triggerElement}
      <PopoverDialog
        ref={setPopoverElement}
        isVisible={isOpen}
        style={{ ...styles.popper, width: menuWidth, overflow: 'hidden' }} // crop highlighted options so they don't spill over the rounded corners
        {...attributes.popper}
      >
        <Menu
          {...getMenuProps({
            role: 'menu',
            ref: menuRef,
            style: { maxHeight: 300 },
          })}
          menuWidth={menuWidth}
        >
          {menuContent}
        </Menu>
        {footerOption && isOpen && (
          <>
            <Divider />
            <MenuItem
              key={itemToValue(footerOption)}
              style={{
                height: itemHeight,
                position: 'sticky',
                bottom: 0,
              }}
              {...getItemProps({
                isItemDisabled: false,
                isSelected: false,
                index: options.length, // Last item points to the footer
                item: footerOption,
                // Since the 'footerOption' sits outside of 'Downshift Menu', usual 'onSelectedItemChange'
                // doesn't work and hence we need to rely on 'onMouseDown' event to update the selected item.
                // https://github.com/downshift-js/downshift/issues/287#issuecomment-512855962
                onMouseDown: () => {
                  onChange(footerOption);
                },
                // downshift expects us to set this manually when using useSelect
                'aria-selected': highlightedIndex === options.length,
              })}
            >
              {itemRenderer(footerOption)}
            </MenuItem>
          </>
        )}
      </PopoverDialog>
    </Box>
  );
}

// Utils
// ------------------------------

export function useSelectPopper({
  isOpen,
  align,
  menuLeftOffset = 0,
}: {
  isOpen: boolean;
  align: AlignType;
  menuLeftOffset?: number;
}) {
  // setup popper (position handling)
  const [anchorElement, setAnchorElement] = useState<AnchorElementType | null>(
    null
  );
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>();

  const { styles, attributes, update } = usePopper(
    anchorElement,
    popoverElement,
    {
      placement: MENU_ALIGNMENT[align],
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [menuLeftOffset, themeRaw.spacing.small],
          },
        },
      ],
    }
  );

  // update popper when it opens to get the latest placement
  // useful for prerendered popovers in modals etc.
  useEffect(() => {
    if (update && isOpen) {
      update();
    }
  }, [isOpen, update]);

  return {
    attributes,
    styles,
    setAnchorElement,
    setPopoverElement,
  };
}
